import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Stack,
    MenuItem,
    CircularProgress,
    Box,
    Typography,
} from '@mui/material';
import { IUser } from '../definitions/app/user.d';
import toast from 'react-hot-toast';
import { addAUser } from 'app/services/users/userAPI';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { FormProvider } from '../components/hook-form';

interface AddUserModalProps {
    open: boolean;
    onClose: () => void;
    onAddUser: (userData: IUser) => void;
}

const AddUserModal: React.FC<AddUserModalProps> = ({ open, onClose, onAddUser }) => {
    const [loading, setLoading] = useState(false);

    const AddUserSchema = Yup.object().shape({
        name: Yup.string().required('Full Name is required'),
        email: Yup.string().email('Email must be a valid email address').required('Email is required'),
        password: Yup.string().min(6, 'Password must be at least 6 characters').required('Password is required'),
        gender: Yup.string().required('Gender is required'),
        role: Yup.string().required('Role is required'),
    });

    const defaultValues: IUser = {
        name: '',
        email: '',
        password: '',
        gender: '',
        role: 'user',
        login_type: 'email',
    };

    const methods = useForm<IUser>({
        resolver: yupResolver(AddUserSchema),
        defaultValues,
        mode: 'onTouched', // Apply validation on blur/change
    });

    const { handleSubmit, reset, control } = methods;

    useEffect(() => {
        if (!open) {
            reset(defaultValues);
        }
    }, [open, reset]);

    const onSubmit = handleSubmit(async (formData) => {
        try {
            setLoading(true);
            const result = await addAUser(formData);
            if (result.status === "success") {
                setLoading(false);
                toast.success("User added successfully");
                onClose();
                onAddUser(formData);
            } else {
                toast.error(result.message || 'Failed to add user');
            }
            setLoading(false);
        } catch (error) {
            //@ts-ignore
            toast.error(error.response?.data?.message || 'Failed to add user ');
        }
    });

    const roles = ['user', 'employee', 'admin', 'manager', 'superadmin'];
    const genders = ['Male', 'Female'];

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>Add New User</DialogTitle>
            <DialogContent>
                <FormProvider methods={methods} onSubmit={onSubmit}>
                    <Stack spacing={2} sx={{ mt: 2 }}>
                        <Controller
                            name="name"
                            control={control}
                            render={({ field, fieldState: { error } }) => (
                                <TextField
                                    {...field}
                                    fullWidth
                                    label="Full Name"
                                    error={!!error}
                                    helperText={error?.message}
                                />
                            )}
                        />
                        <Controller
                            name="email"
                            control={control}
                            render={({ field, fieldState: { error } }) => (
                                <TextField
                                    {...field}
                                    fullWidth
                                    label="Email Address"
                                    type="email"
                                    error={!!error}
                                    helperText={error?.message}
                                />
                            )}
                        />
                        <Controller
                            name="password"
                            control={control}
                            render={({ field, fieldState: { error } }) => (
                                <TextField
                                    {...field}
                                    fullWidth
                                    label="Password"
                                    type="password"
                                    error={!!error}
                                    helperText={error?.message}
                                />
                            )}
                        />
                        <Controller
                            name="gender"
                            control={control}
                            render={({ field, fieldState: { error } }) => (
                                <TextField
                                    {...field}
                                    fullWidth
                                    select
                                    label="Gender"
                                    error={!!error}
                                    helperText={error?.message}
                                >
                                    {genders.map((option) => (
                                        <MenuItem key={option} value={option}>
                                            {option}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            )}
                        />
                        <Controller
                            name="role"
                            control={control}
                            render={({ field, fieldState: { error } }) => (
                                <TextField
                                    {...field}
                                    fullWidth
                                    select
                                    label="Role"
                                    error={!!error}
                                    helperText={error?.message}
                                >
                                    {roles.map((option) => (
                                        <MenuItem key={option} value={option}>
                                            {option}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            )}
                        />
                        {/* Add other fields if necessary, e.g., companyId, userNo */}
                    </Stack>
                </FormProvider>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="inherit">
                    Cancel
                </Button>
                {loading ? (
                    <Box sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 2
                    }}>
                        <CircularProgress sx={{ color: '#1976d2' }} />
                        <Typography variant="body1" color="textSecondary">
                            Loading data...
                        </Typography>
                    </Box>
                ) : (
                    <Button onClick={onSubmit} variant="contained">
                        Add User
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default AddUserModal;
